/**
 * The relationship between a contact and a property, opened.
 *
 * It is a record in its own right — §4.6 says so: `contact_property_links`
 * carries the role, `is_primary`, `is_bill_to`, `approves_quotes`, an access
 * note and the notification grid. The same person is a tenant at one address
 * and the bill-to at another, and every one of those facts lives on the link
 * rather than on either end of it.
 *
 * So this dialog edits the link, all of it, and is opened by clicking the row
 * anywhere. Before, the row navigated to the contact and only the eight dots
 * opened anything — which made four of the six fields on a link unreachable
 * after it was created, and made the one control that did open something the
 * smallest target on the card.
 *
 * **It holds a draft and saves once.** The old one wrote each tick straight to
 * the server and left the card it was drawn from unrefreshed, so the boxes
 * appeared not to move: you ticked three, saw nothing, pressed Done, and the
 * three were there. Everything is local until Save, which is also what makes
 * Cancel mean something.
 */
import { useState } from 'react'
import type { NotificationGrid } from '@rowted/shared'
import { Modal } from './overlays.js'
import { Button, Checkbox, Tag } from './primitives.js'
import { NotificationGridEditor } from './notification-grid.js'
import { ROLE_SUGGESTIONS } from './link-record.js'

export interface EditableLink {
  id: string
  role: string
  isPrimary: boolean
  isBillTo: boolean
  approvesQuotes: boolean
  accessNote: string | null
  effectivePrefs: NotificationGrid
  /** What "back to the contact's defaults" gives — the contact's own grid. */
  defaultPrefs: NotificationGrid
  usingDefaults: boolean
  hasPortalAccess?: boolean
}

export interface LinkPatch {
  role: string
  isPrimary: boolean
  isBillTo: boolean
  approvesQuotes: boolean
  accessNote: string | null
  /** `null` means "go back to the contact's own defaults". */
  notificationPrefs: NotificationGrid | null
}

export function LinkEditor({
  link, title, subtitle, canEdit, onClose, onSave, onRemove, onOpenRecord, openRecordLabel,
}: {
  link: EditableLink
  /** The other end of the link — the person, or the place. */
  title: string
  subtitle?: string
  canEdit: boolean
  onClose: () => void
  onSave: (patch: LinkPatch) => Promise<void>
  onRemove?: () => Promise<void>
  onOpenRecord?: () => void
  openRecordLabel?: string
}) {
  const [role, setRole] = useState(link.role)
  const [isPrimary, setIsPrimary] = useState(link.isPrimary)
  const [isBillTo, setIsBillTo] = useState(link.isBillTo)
  const [approvesQuotes, setApprovesQuotes] = useState(link.approvesQuotes)
  const [accessNote, setAccessNote] = useState(link.accessNote ?? '')
  /*
   * The draft, in the two states the link itself has: `null` is "inherit the
   * contact's defaults", a grid is "this link has its own".
   *
   * It opens on **what the link already is**, which is the whole of Aaron's
   * second and third complaints. It used to open on `null` regardless, so:
   * a link that was already overridden showed no way back to the defaults
   * when you reopened the dialog, because the only thing that revealed the
   * control was having just edited something in this session; and pressing it
   * set the draft to `null` while the grid fell back to `effectivePrefs` —
   * which for an overridden link *is* the override. So the tick you had just
   * changed moved and the other seven did not, and the real reset only
   * appeared after the save came back.
   *
   * Seeded from the link and read against the defaults, both of those stop
   * being possible: the control is there whenever there is something to
   * reset, and pressing it swaps the whole grid in the same render.
   */
  const [prefs, setPrefs] = useState<NotificationGrid | null>(
    link.usingDefaults ? null : link.effectivePrefs)
  const [busy, setBusy] = useState(false)
  const [confirming, setConfirming] = useState(false)

  const grid = prefs ?? link.defaultPrefs

  async function submit() {
    setBusy(true)
    try {
      await onSave({
        role: role.trim() || link.role,
        isPrimary,
        isBillTo,
        approvesQuotes,
        accessNote: accessNote.trim() || null,
        notificationPrefs: prefs,
      })
      onClose()
    } finally {
      setBusy(false)
    }
  }

  if (confirming && onRemove) {
    return (
      <Modal
        open
        onClose={() => setConfirming(false)}
        title="Take this relationship off?"
        footer={(
          <>
            <Button disabled={busy} onClick={() => setConfirming(false)}>Keep it</Button>
            <Button
              variant="primary"
              disabled={busy}
              onClick={async () => {
                setBusy(true)
                try { await onRemove(); onClose() } finally { setBusy(false) }
              }}
            >
              {busy ? 'Removing…' : 'Take it off'}
            </Button>
          </>
        )}
      >
        <p className="m-0 text-body text-text-muted">
          Nothing is deleted. What comes off is the link between this record and {title},
          and with it the notifications that were going out on it — the link itself is
          kept in the history.
        </p>
      </Modal>
    )
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      footer={(
        <>
          {canEdit && onRemove && (
            <Button
              disabled={busy}
              onClick={() => setConfirming(true)}
              className="mr-auto text-danger"
            >
              Remove
            </Button>
          )}
          <Button disabled={busy} onClick={onClose}>Cancel</Button>
          {canEdit && (
            <Button variant="primary" disabled={busy} onClick={() => void submit()}>
              {busy ? 'Saving…' : 'Save'}
            </Button>
          )}
        </>
      )}
    >
      <div className="flex flex-col gap-3.5">
        <p className="m-0 flex flex-wrap items-center gap-1.5 text-chip text-text-muted">
          {subtitle && <span>{subtitle}</span>}
          {link.hasPortalAccess && <Tag>Portal</Tag>}
          {/* What the draft says, not what the server last saved — the badge
              and the grid below it have to agree while you are editing. */}
          <Tag tone="quiet">{prefs === null ? 'Using defaults' : 'Set here'}</Tag>
          {onOpenRecord && (
            <button
              type="button"
              onClick={onOpenRecord}
              className="ml-auto font-semibold text-accent hover:underline"
            >
              {openRecordLabel ?? 'Open record'} ›
            </button>
          )}
        </p>

        <label className="grid grid-cols-[110px_minmax(0,1fr)] items-center gap-3">
          <span className="text-body text-text-muted">Role</span>
          <input
            list="link-editor-roles"
            value={role}
            readOnly={!canEdit}
            onChange={(event) => setRole(event.target.value)}
            placeholder="Owner"
            className="h-8 w-full rounded-field border border-line bg-field px-2 text-body text-text"
          />
          <datalist id="link-editor-roles">
            {ROLE_SUGGESTIONS.map((value) => <option key={value} value={value} />)}
          </datalist>
        </label>

        <div className="grid grid-cols-[110px_minmax(0,1fr)] items-start gap-3">
          <span className="pt-1 text-body text-text-muted">On this record</span>
          <div className="flex flex-col gap-2">
            <Tick
              checked={isPrimary}
              disabled={!canEdit}
              onChange={setIsPrimary}
              label="Main contact"
              hint="Who we call first."
            />
            <Tick
              checked={isBillTo}
              disabled={!canEdit}
              onChange={setIsBillTo}
              label="Bill to"
              hint="Invoices for this work go to them."
            />
            <Tick
              checked={approvesQuotes}
              disabled={!canEdit}
              onChange={setApprovesQuotes}
              label="Approves quotes"
              hint="They can sign a quote in the portal."
            />
          </div>
        </div>

        <div className="grid grid-cols-[110px_minmax(0,1fr)] items-start gap-3">
          <span className="pt-1 text-body text-text-muted">Notified about</span>
          <NotificationGridEditor
            label="Gets notified about"
            grid={grid}
            readOnly={!canEdit}
            onChange={canEdit ? setPrefs : undefined}
          />
        </div>

        {/*
          * "Contact defaults", because that is whose they are: `effectiveGrid`
          * falls back to the *contact's* grid, not to anything the company
          * sets. It said company, which pointed at a screen that has no such
          * setting on it.
          */}
        {canEdit && prefs !== null && (
          <button
            type="button"
            onClick={() => setPrefs(null)}
            className="self-start text-chip font-semibold text-accent hover:underline"
          >
            Back to the contact defaults
          </button>
        )}

        <label className="grid grid-cols-[110px_minmax(0,1fr)] items-start gap-3">
          <span className="pt-1.5 text-body text-text-muted">Note</span>
          <textarea
            value={accessNote}
            readOnly={!canEdit}
            onChange={(event) => setAccessNote(event.target.value)}
            rows={2}
            placeholder="Optional — anything about this person at this place."
            className="w-full resize-none rounded-field border border-line bg-field px-2 py-1.5
                       text-body text-text"
          />
        </label>
      </div>
    </Modal>
  )
}

function Tick({
  checked, disabled, onChange, label, hint,
}: {
  checked: boolean
  disabled: boolean
  onChange: (value: boolean) => void
  label: string
  hint: string
}) {
  return (
    <label className="flex items-start gap-2">
      <span className="pt-0.5">
        <Checkbox checked={checked} disabled={disabled} onChange={() => onChange(!checked)} />
      </span>
      <span className="min-w-0">
        <span className="block text-body text-text">{label}</span>
        <span className="block text-chip text-text-faint">{hint}</span>
      </span>
    </label>
  )
}

